import { useId } from 'react'
import styled from 'styled-components'
import LoyaltyBenefitIcon from 'loyalty/components/LoyaltyBenefitIcon'
// Stopgap: the gradient stops use the primitive ramp directly because DS doesn't expose them as
// themeable tokens yet (see JSDoc below). Swap for the styled-components theme once it does.
// oxlint-disable-next-line no-restricted-imports
import { INFINITE } from 'constants/themes/primitiveColours'

/**
 * A loyalty benefit icon filled with the Infinite rose-gold gradient (matches the tertiary gradient
 * used by the Société swash/logo).
 *
 * The benefit line icons hardcode `fill="currentColor"` on their paths, and an SVG path can't take a
 * CSS gradient as a colour — only a solid colour or a `url(#id)` paint server. So we define the
 * gradient in a hidden `<defs>` and override the path fill with `fill: url(#id)` via CSS, which wins
 * over the presentation attribute.
 *
 * NOTE: the stop colours reference the raw `INFINITE` primitive ramp directly. This is a stopgap —
 * the design system does not currently expose the tertiary gradient's stops as semantic palette
 * tokens (only the composed `gradientTertiaryBackground` CSS string, which can't be used as SVG
 * `<stop>`s). Once DS provides gradient-stop tokens, replace these primitives with
 * `theme.palette.product.loyalty.infinite.*` so the colours stay themeable.
 */
const GradientDefs = styled.svg`
  position: absolute;
  width: 0;
  height: 0;
  overflow: hidden;

  --infinite-gradient-edge: ${INFINITE[300]};
  --infinite-gradient-centre: ${INFINITE[100]};
`

const FILL_VAR = '--loyalty-benefit-icon-fill'

const Container = styled.span`
  display: inline-flex;

  & path {
    fill: var(${FILL_VAR});
  }
`

interface Props {
  benefitType: App.LoyaltyBenefitType
}

function LoyaltyInfiniteBenefitIcon({ benefitType }: Props) {
  const gradientId = useId()

  return (
    <Container style={{ [FILL_VAR]: `url(#${gradientId})` }}>
      <GradientDefs aria-hidden="true">
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="100%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="var(--infinite-gradient-edge)" />
            <stop offset="45%" stopColor="var(--infinite-gradient-centre)" />
            <stop offset="84%" stopColor="var(--infinite-gradient-edge)" />
          </linearGradient>
        </defs>
      </GradientDefs>
      <LoyaltyBenefitIcon benefitType={benefitType} />
    </Container>
  )
}

export default LoyaltyInfiniteBenefitIcon
